import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CurrentUserStore } from '@pinnakl/core/data-providers';
import { appApiAccess, EnvironmentService } from '@pinnakl/core/environment';
import {
  AmplitudePOEMSEventTypes,
  AppNames,
  AppNamesTitleLabels,
  CurrentUserInfoParsed,
  CurrentUserInfoUser,
  UserTypes,
} from '@pinnakl/shared/types';
import { AmplitudeEventsCollector } from '@pinnakl/shared/util-external-services';
import { getAppInstanceToken, getErrorMessage } from '@pinnakl/shared/util-helpers';
import { PinnaklSpinnerService, PinnaklUIToastMessage, SessionExpirationService } from '@pinnakl/shared/util-providers';
import {
  AuthStateResult,
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
  ValidationResult,
} from 'angular-auth-oidc-client';
import { BehaviorSubject, forkJoin, from, Observable, of, ReplaySubject, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take, tap, timeout } from 'rxjs/operators';
import { AuthFailure, DEFAULTSCREEN, USERTYPE } from './models';

interface Claim {
  sub: string;
  oi_au_id: string;
  azp: string;
  nonce: string;
  at_hash: string;
  oi_tkn_id: string;
  aud: string;
  exp: number;
  iss: string;
  iat: number;
}

type AuthCheckResult = { accessToken: string; userData: Claim | null } | null;

type LoginMode = 'foreground' | 'background';

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  return typeof value === 'string' ? [value] : [];
}

export const AUTH_RESOLUTION_TIMEOUT_MS = 20_000;
const MAX_AUTHORIZE_ATTEMPTS = 5;
const AUTHORIZE_ATTEMPTS_KEY = 'pnkl-auth-authorize-attempts';
const CONCURRENT_RENEW_POLL_MS = 500;
const CONCURRENT_RENEW_TIMEOUT_MS = 20_000;

type FocusSessionState = 'valid' | 'expired' | 'unknown';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly defaultScreen = inject<{
    prod: string;
    dev: string;
  }>(DEFAULTSCREEN);
  private readonly userType = inject<UserTypes>(USERTYPE);
  private readonly environmentService = inject(EnvironmentService);
  private readonly router = inject(Router);
  private readonly toast = inject(PinnaklUIToastMessage);
  private readonly spinner = inject(PinnaklSpinnerService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly eventService = inject(PublicEventsService);
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly currentUserStore = inject(CurrentUserStore);
  private readonly amplitudeEventsCollector = inject(AmplitudeEventsCollector);
  private readonly sessionExpirationService = inject(SessionExpirationService);
  private version = this.environmentService.version;

  private ROUTE_AFTER_LOGIN = '/';

  private completingLogin = false;
  private processedAuthCode: string | null = null;
  private refreshInFlight$: Observable<string> | null = null;
  private authCodeExchange$: ReplaySubject<string> | null = null;

  refreshingAuthState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  userData$: BehaviorSubject<Claim | null> = new BehaviorSubject<Claim | null>(null);
  otpSecret$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  isAccountReady$: Observable<boolean> = this.oidcSecurityService.isAuthenticated$.pipe(
    switchMap(result => {
      const isAuthenticated = !!result?.isAuthenticated;
      return isAuthenticated
        ? toObservable(this.currentUserStore.currentUserInfo, { injector: this.injector }).pipe(map(Boolean))
        : of(isAuthenticated);
    }),
  );
  isLoggingOff$ = new BehaviorSubject(false);
  readonly authFailure = signal<AuthFailure | null>(null);
  readonly appAccessDenied = signal(false);
  baseUrl?: string;
  application = 'Desktop';
  productionMode = false;
  appName?: AppNames;

  get configId(): string {
    const authConfig = this.environmentService.get('authConfig');
    return getAppInstanceToken(authConfig?.configId, location?.host, this.environmentService.get('appName'));
  }

  get authUrls(): Record<
    'login' | 'linkLogin' | 'generateLink' | 'verifyotp' | 'verifyQrOtp' | 'clients' | 'selectClient',
    string
  > {
    return {
      login: `${this.baseUrl}/account/login`,
      clients: `${this.baseUrl}/account/clients`,
      selectClient: `${this.baseUrl}/account/select-client`,
      linkLogin: `${this.baseUrl}/account/link/login`,
      generateLink: `${this.baseUrl}/account/link`,
      verifyotp: `${this.baseUrl}/account/verifyotp`,
      verifyQrOtp: `${this.baseUrl}/account/totp/verify`,
    };
  }

  get authWellKnownEndPoints(): Record<string, string> {
    try {
      const authData = localStorage.getItem(this.configId);
      const authDataParsed = JSON.parse(authData ?? '{}');
      const endpoints = authDataParsed?.authWellKnownEndPoints ?? {};
      const { issuer, ...rest } = endpoints;
      return { ...rest, ...this.authUrls };
    } catch {
      return {};
    }
  }

  get isLoggingOff(): boolean {
    return this.isLoggingOff$.value;
  }

  set isLoggingOff(value: boolean) {
    this.isLoggingOff$.next(value);
  }

  selectClientInAuthApp(): void {
    if (!this.claimAuthorizeAttempt()) {
      this.log({
        action: 'AuthService:selectClientInAuthApp:suppressed-redirect-loop',
      });
      this.reportSignInBlocked(
        "We couldn't switch client",
        'Switching client kept bouncing back without a session, so it has been stopped. Try again, or sign out and back in if it keeps failing.',
      );
      return;
    }
    this.oidcSecurityService.authorize(this.configId, {
      customParams: {
        prompt: 'select_client',
      },
      redirectUrl: window.location.origin,
    });
  }

  init(): void {
    this.log({
      action: 'AuthService:init',
    });
    this.productionMode = this.environmentService.get('production');
    this.baseUrl = this.environmentService.get('authConfig')?.authority;
    this.appName = this.environmentService.get('appName');
    this.checkAppVersion();
    this.ROUTE_AFTER_LOGIN = this.productionMode ? this.defaultScreen.prod : this.defaultScreen.dev;
    window.onfocus = () => this.checkUserSession();
    this.listenToAccessTokenRenew();
  }

  isOidcAuthenticated(): Observable<boolean> {
    return this.oidcSecurityService.isAuthenticated();
  }

  localLogout() {
    this.logout(true);
  }

  logout(local = false): boolean {
    if (this.isLoggingOff) return false;
    this.isLoggingOff = true;
    const user = this.currentUserStore.currentUser();
    user?.firstName && localStorage.setItem('name', user?.firstName);
    !local && this.spinner.spin('auth');
    this.currentUserStore.removeUser();
    this.appAccessDenied.set(false);
    if (local) {
      this.oidcSecurityService.logoffLocal();
      this.trackLogout(user, local);
      if (!this.redirectToAuthApp()) {
        this.isLoggingOff = false;
        return false;
      }
    } else {
      const logoutAuthOptions = { customParams: { redirect_uri: window.location.origin } };
      this.oidcSecurityService
        .logoffAndRevokeTokens(this.configId, logoutAuthOptions)
        .pipe(
          catchError((error: unknown) => {
            this.log({
              action: `AuthService:logout:logoffAndRevokeTokens:error=${String(error)}`,
            });
            return this.oidcSecurityService.logoff(this.configId, logoutAuthOptions);
          }),
        )
        .subscribe({
          next: () => this.trackLogout(user, local),
          error: () => {
            this.isLoggingOff = false;
            this.localLogout();
          },
        });
    }
    return true;
  }

  forceLogout(): void {
    if (!this.claimAuthorizeAttempt()) {
      this.reportBackstop('force-logout-budget-exhausted');
      this.reportSignInBlocked();
      return;
    }
    this.isLoggingOff = false;
    this.logout();
  }

  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  hasAppApiAccess(): Observable<boolean> {
    const access = appApiAccess[this.environmentService.get<AppNames>('appName')];
    if (!access) return of(true);
    return this.oidcSecurityService.getPayloadFromAccessToken(false, this.configId).pipe(
      take(1),
      map(payload => toStringArray(payload?.aud)),
      catchError(() => of<string[]>([])),
      switchMap(audiences =>
        audiences.length ? of(audiences.includes(access.audience)) : this.hasGrantedScopes(access.scopes),
      ),
    );
  }

  reportAppAccessDenied(): void {
    if (this.appAccessDenied()) return;
    this.appAccessDenied.set(true);
    const appLabel = AppNamesTitleLabels[this.environmentService.get<AppNames>('appName')];
    this.toast.warning(`Your account does not have access to ${appLabel}. Contact your administrator.`);
  }

  getRefreshedAccessToken(): Observable<string> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    if (this.completingLogin && this.authCodeExchange$) {
      return this.authCodeExchange$.pipe(take(1), timeout({ first: AUTH_RESOLUTION_TIMEOUT_MS }));
    }

    this.refreshingAuthState.next(true);
    this.refreshInFlight$ = forkJoin([
      this.oidcSecurityService.getAccessToken(this.configId).pipe(take(1)),
      this.oidcSecurityService.getRefreshToken(this.configId).pipe(take(1)),
    ]).pipe(
      switchMap(([staleToken, refreshToken]) => {
        if (!refreshToken) return throwError(() => new Error('No refresh token to renew the session with'));
        return this.oidcSecurityService
          .forceRefreshSession(undefined, this.configId)
          .pipe(
            switchMap(response =>
              response?.accessToken && response.accessToken !== staleToken
                ? of(response.accessToken)
                : this.waitForConcurrentRenew(staleToken),
            ),
          );
      }),
      tap({
        error: (error: unknown) =>
          this.log({
            action: `AuthService:getRefreshedAccessToken:error=${String(error)}`,
          }),
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
        this.refreshingAuthState.next(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.refreshInFlight$;
  }

  updateQrSecret(callback: () => void): void {
    this.loadOtpSecret('secret').subscribe(otpSecret => {
      if (!otpSecret) return;
      this.otpSecret$.next(otpSecret);
      callback();
    });
  }

  loadCurrentOtpSecret(): void {
    this.loadOtpSecret('secret/current').subscribe(otpSecret => {
      if (otpSecret && !this.otpSecret$.value) this.otpSecret$.next(otpSecret);
    });
  }

  passwordResetDone() {
    this.checkAuthAndGetUser();
  }

  navigateAsAuthUser() {
    this.log({
      action: 'AuthService:navigateAsAuthUser',
    });
    this.router.navigate([this.ROUTE_AFTER_LOGIN]).then(() => {
      this.spinner.stop('auth');
    });
  }

  redirectToAuthApp(): boolean {
    if (!this.claimAuthorizeAttempt()) {
      this.log({
        action: 'AuthService:redirectToAuthApp:suppressed-redirect-loop',
      });
      this.spinner.stop('auth');
      this.spinner.stop();
      this.reportBackstop('authorize-attempt-budget-exhausted');
      this.reportSignInBlocked();
      return false;
    }
    this.startAuth();
    return true;
  }

  retryAuth(): void {
    if (!this.authFailure()) return;
    this.clearAuthorizeAttempts();
    this.redirectToAuthApp();
  }

  reportBackstop(reason: string): void {
    console.error(`[auth backstop] ${reason} - href=${window.location.href}`);
    this.log({
      action: `AuthService:backstop:${reason}`,
    });
  }

  get isCompletingLogin(): boolean {
    return this.completingLogin;
  }

  private get authCodeInUrl(): string | null {
    return new URLSearchParams(new URL(window.location.href).search).get('code');
  }

  checkAuthCodeAndProceedAuth(): void {
    const code = this.authCodeInUrl;
    if (!code || code === this.processedAuthCode || this.completingLogin) return;
    this.processedAuthCode = code;
    this.completingLogin = true;
    this.authCodeExchange$ = new ReplaySubject<string>(1);
    this.checkAuthAndGetUser();
  }

  silentlyFetchUser(): void {
    if (this.isLoggingOff) return;
    this.checkAuthAndGetUser('background');
  }

  log(message): void {
    if (this.environmentService.isDevEnv && JSON.parse(localStorage.getItem('auth-logging') ?? '""')) {
      console.info(message.action);
    }
  }

  generateResetLink(email: string): void {
    this.spinner.spin('auth');
    const endpoint = `${this.authUrls.generateLink}`;
    const returnUrl = `${this.ROUTE_AFTER_LOGIN}`;

    this.http.post<any>(endpoint, { email, usertype: this.userType, returnUrl }).subscribe({
      next: () => {
        this.spinner.stop('auth');
        this.toast.success(
          'A password reset link has been sent to your email. Click on the link to reset your password.',
        );
      },
      error: () => {
        this.spinner.stop('auth');
        this.toast.error('Failed to generate reset link');
      },
    });
  }

  private startAuth(): void {
    this.log({
      action: `AuthService:startAuth:config=${JSON.stringify(localStorage.getItem(this.configId))}`,
    });
    this.oidcSecurityService.authorize(this.configId, {
      urlHandler: url => {
        this.spinner.spin('auth');
        window.location.href = url;
      },
    });
  }

  private claimAuthorizeAttempt(): boolean {
    return this.withSessionStorage(() => {
      const stored = Number(sessionStorage.getItem(AUTHORIZE_ATTEMPTS_KEY));
      const attempts = (Number.isFinite(stored) ? stored : 0) + 1;
      if (attempts > MAX_AUTHORIZE_ATTEMPTS) return false;
      sessionStorage.setItem(AUTHORIZE_ATTEMPTS_KEY, String(attempts));
      return true;
    }, true);
  }

  private restartSignInAfterFailedCallback(): boolean {
    this.log({
      action: 'AuthService:restartSignInAfterFailedCallback',
    });
    this.oidcSecurityService.logoffLocal();
    return this.redirectToAuthApp();
  }

  private clearAuthorizeAttempts(): void {
    this.withSessionStorage(() => sessionStorage.removeItem(AUTHORIZE_ATTEMPTS_KEY), undefined);
  }

  private withSessionStorage<T>(action: () => T, fallback: T): T {
    try {
      return action();
    } catch {
      return fallback;
    }
  }

  private settleAuthCodeExchange(accessToken: string): void {
    this.authCodeExchange$?.next(accessToken);
    this.authCodeExchange$?.complete();
  }

  private failAuthCodeExchange(error: unknown): void {
    this.authCodeExchange$?.error(error);
  }

  private reportSignInBlocked(
    title = "We couldn't complete sign in",
    message = 'Sign in kept coming back without a session, so it has been stopped rather than looped. Try again, or contact support if it keeps failing.',
  ): void {
    this.spinner.stop('auth');
    this.spinner.stop();
    this.authFailure.set({ title, message });
  }

  private checkAuthAndGetUser(mode: LoginMode = 'foreground'): void {
    this.log({
      action: `AuthService:checkAuthAndGetUser:config=${JSON.stringify(localStorage.getItem(this.configId))}`,
    });
    mode === 'foreground' && this.spinner.spin('auth');
    this.refreshingAuthState.next(true);
    this.checkAuthentication()
      .pipe(
        tap(result => this.releaseTokenWaiters(result)),
        switchMap(result => this.loadAccount(result)),
        finalize(() => this.settleLogin()),
      )
      .subscribe(userInfo => this.finishLogin(userInfo, mode));
  }

  private checkAuthentication(): Observable<AuthCheckResult> {
    return this.oidcSecurityService.checkAuth().pipe(
      take(1),
      catchError((error: unknown) => {
        this.log({
          action: `AuthService:checkAuthAndGetUser:checkAuth:error=${String(error)}`,
        });
        this.failAuthCodeExchange(error);
        if (this.authCodeInUrl && this.restartSignInAfterFailedCallback()) return of(null);
        this.router.navigate([this.ROUTE_AFTER_LOGIN], { replaceUrl: true });
        return of(null);
      }),
    );
  }

  private releaseTokenWaiters(result: AuthCheckResult): void {
    this.refreshingAuthState.next(false);
    if (result?.accessToken) this.settleAuthCodeExchange(result.accessToken);
  }

  private loadAccount(result: AuthCheckResult): Observable<CurrentUserInfoParsed | null> {
    this.log({
      action: `AuthService:checkAuthAndGetUser:checkAuth:switchMap=${JSON.stringify(result)}`,
    });
    if (result?.userData) {
      this.userData$.next(result.userData);
    }
    if (!result?.accessToken) return of(null);
    this.clearAuthorizeAttempts();
    return this.getUserInfo(result.accessToken);
  }

  private settleLogin(): void {
    this.completingLogin = false;
    this.failAuthCodeExchange(new Error('Auth code exchange produced no access token'));
    this.authCodeExchange$ = null;
    this.refreshingAuthState.next(false);
  }

  private finishLogin(userInfo: CurrentUserInfoParsed | null, mode: LoginMode): void {
    this.log({
      action: `AuthService:checkAuthAndGetUser:checkAuth:subscribe=${JSON.stringify(userInfo)}`,
    });
    if (!userInfo) {
      this.refreshingAuthState.next(false);
      this.spinner.stop('auth');
      return;
    }
    localStorage.setItem('name', userInfo.user.firstName);
    this.sessionExpirationService.reset();
    this.clearAuthorizeAttempts();
    this.authFailure.set(null);
    this.appAccessDenied.set(false);
    this.refreshingAuthState.next(false);
    if (mode === 'foreground') {
      this.navigateAsAuthUser();
    } else {
      this.spinner.stop('auth');
    }
  }

  private loadOtpSecret(path: 'secret' | 'secret/current'): Observable<string | null> {
    return this.http.get(`${this.baseUrl}/account/totp/${path}`, { responseType: 'text', withCredentials: true }).pipe(
      map(otpSecret => otpSecret.replace(/^"|"$/g, '') || null),
      catchError((error: unknown) => {
        console.error('Error fetching OTP secret. ' + getErrorMessage('', error));
        return of(null);
      }),
    );
  }

  private setCurrentAppVersion() {
    localStorage.setItem('appVersion', this.version);
  }

  private isLatestAppVersion(): boolean {
    const appVersion = localStorage.getItem('appVersion');
    if (!appVersion) return false;
    return this.version === appVersion;
  }

  private checkAppVersion() {
    if (!this.isLatestAppVersion()) {
      this.currentUserStore.removeUser();
    }
    this.setCurrentAppVersion();
  }

  private trackLogout(user: CurrentUserInfoUser | null, local: boolean): void {
    this.amplitudeEventsCollector.track(AmplitudePOEMSEventTypes.TemporaryEventLogout, {
      user,
      local,
      appName: this.appName,
      href: window.location.href,
    });
  }

  private checkUserSession() {
    if (
      this.completingLogin ||
      this.isLoggingOff ||
      this.refreshingAuthState.getValue() ||
      this.currentUserStore.currentUserInfoLoading() ||
      !this.currentUserStore.currentUserInfo()
    ) {
      return;
    }
    this.oidcSecurityService
      .checkAuth()
      .pipe(
        take(1),
        map((result): FocusSessionState => (result?.isAuthenticated ? 'valid' : 'expired')),
        catchError((error: unknown) => {
          this.log({
            action: `AuthService:checkUserSession:checkAuth:error=${String(error)}`,
          });
          return of<FocusSessionState>('expired');
        }),
        switchMap(state => (state === 'expired' ? this.refreshExpiredSession() : of(state))),
      )
      .subscribe(state => {
        if (state === 'unknown') return;
        if (state === 'expired' || !this.currentUserStore.currentUserInfo()) {
          this.localLogout();
          return;
        }
        this.currentUserStore.getCurrentUserInfo(false);
      });
  }

  private refreshExpiredSession(): Observable<FocusSessionState> {
    return this.getRefreshedAccessToken().pipe(
      map((): FocusSessionState => 'valid'),
      catchError(() =>
        this.oidcSecurityService.getRefreshToken(this.configId).pipe(
          take(1),
          map((refreshToken): FocusSessionState => (refreshToken ? 'unknown' : 'expired')),
        ),
      ),
    );
  }

  private waitForConcurrentRenew(staleToken: string): Observable<string> {
    return timer(0, CONCURRENT_RENEW_POLL_MS).pipe(
      switchMap(() => this.oidcSecurityService.getAccessToken(this.configId).pipe(take(1))),
      filter(token => token !== staleToken),
      take(1),
      timeout({ first: CONCURRENT_RENEW_TIMEOUT_MS }),
      switchMap(token => (token ? of(token) : throwError(() => new Error('The concurrent session renew failed')))),
    );
  }

  private listenToAccessTokenRenew() {
    this.eventService
      .registerForEvents()
      .pipe(
        filter(notification => notification.type === EventTypes.NewAuthenticationResult),
        map(result => {
          const {
            value: { isAuthenticated, validationResult },
          } = result as { value: AuthStateResult };
          return isAuthenticated && validationResult === ValidationResult.Ok;
        }),
        switchMap(isAuthenticated => {
          if (isAuthenticated) return of({ isAuthenticated });
          this.refreshingAuthState.next(true);
          return this.oidcSecurityService.forceRefreshSession().pipe(
            catchError((error: unknown) => {
              this.log({
                action: `AuthService:listenToAccessTokenRenew:forceRefreshSession:error=${String(error)}`,
              });
              return of({ isAuthenticated: false });
            }),
          );
        }),
      )
      .subscribe(result => {
        const isAuthenticated = !!result?.isAuthenticated;
        if (isAuthenticated) {
          !this.refreshingAuthState.getValue() && this.checkAuthAndGetUser('background');
        } else {
          this.localLogout();
        }
        this.refreshingAuthState.next(!isAuthenticated);
      });
    this.eventService
      .registerForEvents()
      .pipe(filter(notification => [EventTypes.TokenExpired, EventTypes.IdTokenExpired].includes(notification.type)))
      .subscribe(() => this.refreshingAuthState.next(true));
  }

  private getUserInfo(accessToken: string): Observable<CurrentUserInfoParsed | null> {
    this.log({
      action: `AuthService:getUserInfo`,
    });
    return from(
      this.currentUserStore.getCurrentUserInfo(true, accessToken).catch((error: unknown) => {
        console.error('Error in getting user info. ' + getErrorMessage('No error message.', error));
        this.forceLogout();
        return null;
      }),
    );
  }

  private hasGrantedScopes(scopes: string[]): Observable<boolean> {
    return this.oidcSecurityService.getAuthenticationResult(this.configId).pipe(
      take(1),
      map(result => {
        const grantedScopes = typeof result?.scope === 'string' ? result.scope.split(' ') : null;
        return grantedScopes ? scopes.every(scope => grantedScopes.includes(scope)) : true;
      }),
      catchError(() => of(true)),
    );
  }

  private handleApiError(e, toastMessage: string): Observable<any> {
    this.spinner.stop('auth');
    this.toast.error(toastMessage);
    return throwError(() => e);
  }
}
